import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { useGetShopCategoriesQuery } from "slices/eCommerce/shopCatSlice";

function AddForm({ open, onClose, initialData, onSubmit }) {
  const { data, isLoading, refetch } = useGetShopCategoriesQuery({});

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewUrl2, setPreviewUrl2] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: initialData?._id || "",
      category: initialData?.category || "",
      name: initialData?.name || "",
      image: initialData?.image || null,
      metaTitle: initialData?.metaTitle || "",
      metaDescription: initialData?.metaDescription || "",
      keywords: initialData?.keywords || [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Brand name is required"),
      category: Yup.string().required("Category is required"),
      metaTitle: Yup.string().required("Meta title is required"),
      metaDescription: Yup.string().required("Meta description is required"),

      image: Yup.mixed()
        .nullable()
        .test("fileRequired", "Image is required", function (value) {
          return initialData?.image || value;
        }),
    }),

    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
  }, [initialData]);

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formik.values.keywords.includes(trimmed)) {
      formik.setFieldValue("keywords", [...formik.values.keywords, trimmed]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k !== kw)
    );
  };

  const handleFileChange = (field, file) => {
    formik.setFieldValue(field, file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    setPreviewUrl("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initialData ? "Edit" : "Add"}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <FormControl
            fullWidth
            margin="normal"
            required
            error={formik.touched.category && Boolean(formik.errors.category)}
          >
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              name="category"
              labelId="category-label"
              value={formik.values.category || ""}
              label="Category"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {data?.data && data.data.length > 0 ? (
                data.data.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No categories found</MenuItem>
              )}
            </Select>
            {formik.touched.category && formik.errors.category && (
              <Typography color="error" variant="caption">
                {formik.errors.category}
              </Typography>
            )}
          </FormControl>

          <TextField
            name="name"
            label="Brand Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            fullWidth
            margin="normal"
          />

          <Typography variant="h6">Image</Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange("image", e.target.files[0])}
            style={{ marginTop: 16 }}
          />
          {formik.touched.image && formik.errors.image && (
            <Typography color="error" variant="caption">
              {formik.errors.image}
            </Typography>
          )}
          {previewUrl && (
            <img
              crossOrigin="anonymous"
              src={previewUrl}
              alt="Preview"
              style={{
                marginTop: 16,
                width: "100px",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}

          <TextField
            name="metaTitle"
            label="Meta Title"
            value={formik.values.metaTitle}
            onChange={formik.handleChange}
            error={formik.touched.metaTitle && Boolean(formik.errors.metaTitle)}
            helperText={formik.touched.metaTitle && formik.errors.metaTitle}
            fullWidth
            margin="normal"
          />

          <TextField
            name="metaDescription"
            label="Meta Description"
            value={formik.values.metaDescription}
            onChange={formik.handleChange}
            error={
              formik.touched.metaDescription &&
              Boolean(formik.errors.metaDescription)
            }
            helperText={
              formik.touched.metaDescription && formik.errors.metaDescription
            }
            fullWidth
            margin="normal"
          />

          <Box display="flex" alignItems="center" mt={2} mb={1} gap={1}>
            <TextField
              label="Add Keyword"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              fullWidth
            />
            <Button onClick={addKeyword} variant="contained" color="primary">
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {formik.values?.keywords?.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                onDelete={() => removeKeyword(keyword)}
                color="primary"
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained">
            {initialData ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddForm;
